/**
 * EntityCard — Generic entity card component
 *
 * Works for ALL 25 FERP primitives AND subgraph entities (FHIR, ERP, Payroll)
 * via the EntityFieldConfig adapter interface.
 *
 * Uses EntityMediaHeader (DRY) for the hero area — same component as
 * EntityDetailPanel title and EntityInlineDetail header.
 */

import { Card, Group, Text, Badge } from '@mantine/core';
import { EntityMediaHeader } from './EntityMediaHeader';

/**
 * Adapter interface — decouples entity data shape from card display.
 * TabulatorPrimitiveManager provides primitiveFieldAdapter.
 * SubgraphEntityBrowser provides subgraphFieldAdapter.
 */
export interface EntityFieldConfig {
  name: string;
  subtitle?: string;
  status?: string;
  safetyLevel?: string;
  imageUrl?: string;
  /** Key-value pairs shown as metadata below the header */
  metaFields?: Array<{ label: string; value: string }>;
  /** Mantine color token for accent (phase color, subgraph color) */
  accentColor?: string;
  /** Icon component for the avatar fallback */
  Icon?: React.ComponentType<{ size: number }>;
}

interface EntityCardProps {
  fields: EntityFieldConfig;
  isSelected?: boolean;
  onClick?: () => void;
  compact?: boolean;
  /** DRR-189: Optional action slot rendered below metadata (e.g., Complete button) */
  actionSlot?: React.ReactNode;
}

const STATUS_COLOR_MAP: Record<string, string> = {
  ACTIVE: 'green',
  PENDING: 'yellow',
  COMPLETED: 'green',
  FAILED: 'red',
  ARCHIVED: 'gray',
  DRAFT: 'blue',
  PAUSED: 'gray',
  IN_PROGRESS: 'blue',
};

export function EntityCard({ fields, isSelected, onClick, compact, actionSlot }: EntityCardProps) {
  return (
    <Card
      withBorder
      padding={compact ? 'xs' : 'sm'}
      radius="md"
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        borderLeft: fields.accentColor
          ? `3px solid var(--mantine-color-${fields.accentColor}-5)`
          : undefined,
        outline: isSelected
          ? '2px solid var(--mantine-color-blue-5)'
          : undefined,
        outlineOffset: isSelected ? -2 : undefined,
        transition: 'box-shadow 0.15s ease, outline 0.15s ease',
      }}
      className="ferp-entity-card"
    >
      <EntityMediaHeader
        name={fields.name}
        subtitle={fields.subtitle}
        status={fields.status}
        statusColorMap={STATUS_COLOR_MAP}
        safetyLevel={fields.safetyLevel}
        imageUrl={fields.imageUrl}
        accentColor={fields.accentColor}
        Icon={fields.Icon}
        compact
      />

      {/* Metadata fields — up to 4 key-value pairs */}
      {fields.metaFields && fields.metaFields.length > 0 && (
        <Group gap="xs" mt="xs" wrap="wrap">
          {fields.metaFields.slice(0, 4).map((meta) => (
            <Badge
              key={meta.label}
              size="xs"
              variant="outline"
              color="gray"
              styles={{
                label: { textTransform: 'none', fontWeight: 400 },
              }}
            >
              {meta.label}: {meta.value}
            </Badge>
          ))}
        </Group>
      )}

      {/* DRR-189: Action slot for inline operations (e.g., task completion) */}
      {actionSlot}
    </Card>
  );
}
